/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

export type AgencyMember = {
  email?: string | null;
  role?: string | null;
  /**
   * Which kind of place this membership holds. `"support"` is an auxiliary
   * user: free, brought along by a paid seat, and outside the two modules
   * the catalogue meters per seat. Absent on every membership written
   * before auxiliary users existed, so absence reads as `"plan"` — never
   * turn silence into a free place.
   *
   * It belongs to the membership, not to the person: the same human can be
   * a paid broker in one agency and an auxiliary user in another at the
   * same time, so this is always read against the agency on screen.
   */
  seat_kind?: string | null;
};

export type AgencyWithMembers = {
  members?: AgencyMember[] | null;
  /**
   * Modules an auxiliary user cannot open here, published by the backend so
   * the front hides exactly what the backend refuses. Not a constant on this
   * side on purpose: a second copy of the list in another language agrees on
   * the day it is written and not much longer.
   */
  support_blocked_modules?: string[] | null;
};

export type PermissionAction =
  // Agency
  | "edit_agency"
  | "create_agency"
  | "edit_plan_agency"
  | "manage_agency"
  | "view_agency"
  | "view_agency_settings"
  | "invite_agency_member"
  // Member
  | "edit_member"
  | "edit_member_role"
  // Sale [Plano 4]
  | "manage_sale"
  | "view_sale"
  // Rent/Lease
  | "manage_rent"
  | "view_rent"
  // Property registration settings
  | "manage_property_settings"
  // Client
  | "add_client"
  // Spreadsheets
  | "import_spreadsheet"
  // Property
  | "add_property"
  | "edit_property"
  | "delete_property"
  | "view_property"
  // Image
  | "add_image"
  // Reports
  | "view_dashboards"
  // Visits
  | "add_visit"
  | "edit_visit"
  | "view_visits"
  | "delete_visit"
  | "manage_visits";

export type UserRole = "owner" | "admin" | "operational" | "broker" | "anonymus";

/**
 * Permission hierarchy definition
 * Each role inherits permissions from lower roles
 */
export const PERMISSION_HIERARCHY: Record<UserRole, PermissionAction[]> = {
  anonymus: [
    "view_property",
  ],
  broker: [
    // Broker specific
    "add_client",
    "add_property",
    "edit_property",
    "delete_property",
    "add_image",
    "view_dashboards",
    "view_agency",
    "add_visit",
    "edit_visit",
    "view_visits",
    "delete_visit",
    "manage_visits",
    // Sale: read only, same split as rent/lease [Plano 4]
    "view_sale",
    // Lease: read only. Mirrors role_permissions[BROKER] in
    // backend/housien/authorization/permissions.py, where the name is the
    // legacy `rent` rather than `lease`.
    "view_rent",
  ],
  operational: [
    // Operational specific
    "edit_agency",
    // Porta da área de Configurações da imobiliária. Distinta de
    // `view_agency` de propósito: no backend aquela protege
    // GET /agencies/<id>, a busca que toda página faz, e o broker precisa
    // dela. Espelha role_permissions[OPERATIONAL] em
    // backend/housien/authorization/permissions.py.
    "view_agency_settings",
    "invite_agency_member",
    "edit_member",
    "manage_agency",
    // Sale: concluding/cancelling/editing the paperwork vocabulary is an
    // operational act; a broker only reads [Plano 4]
    "manage_sale",
    // Lease: activating, closing and configuring the due-date defaults are
    // operational acts; a broker only reads.
    "manage_rent",
    // Imóveis: which fields a broker must fill before saving a property.
    // Mirrors role_permissions[OPERATIONAL] in
    // backend/housien/authorization/permissions.py.
    "manage_property_settings",
    // Planilhas: importing creates hundreds of records in one commit and
    // re-keys existing people by match, which is not a broker's act. Mirrors
    // role_permissions[OPERATIONAL] in
    // backend/housien/authorization/permissions.py. Exporting is not here:
    // it is gated on reading, and a broker exports their own clients.
    "import_spreadsheet",
  ],
  admin: [
    // Admin specific
    "create_agency",
    "edit_plan_agency",
    // Operational edits a member's data; promoting/demoting is an admin act
    "edit_member_role",
  ],
  owner: [
    // Owner adds nothing beyond admin today; the entry exists so the role is
    // representable and can diverge later.
  ],
};
